import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
function TimeLine({ className = '', date = moment(), meetings = [], status, timeLineLimit = 12, timeLineGap = 2, timeLineUnits = 'hours', simpleTimeFormat = 'hA', currentTimeFormat = 'hh:mmA', minutesDivider, widgetCount, timeZoneId, }) {
    const { t } = useTranslation();
    const timeZone = timeZoneId;
    const getTimelineWindow = (currentMomentValue) => {
        const currentHourValue = currentMomentValue.hour();
        const previousEvenHour = currentHourValue - (currentHourValue % timeLineGap);
        const timelineEndHour = previousEvenHour + timeLineLimit + timeLineGap * 2;
        return {
            startHour: previousEvenHour,
            endHour: timelineEndHour,
            startTime: currentMomentValue
                .clone()
                .hours(previousEvenHour)
                .startOf('hour'),
        };
    };
    const [currentMoment, setCurrentMoment] = useState(moment().tz(timeZone).clone());
    const initialTimelineWindow = getTimelineWindow(currentMoment);
    const [startTime, setStartTime] = useState(initialTimelineWindow.startTime);
    const [startHour, setStartHour] = useState(initialTimelineWindow.startHour);
    const [endHour, setEndHour] = useState(initialTimelineWindow.endHour);
    const [hoursList, setHoursList] = useState(getHoursList(initialTimelineWindow.startHour, initialTimelineWindow.endHour));
    const currentTimelineHour = currentMoment.hour() - (currentMoment.hour() % timeLineGap);
    const [maxMeetingLineHeight, setMaxMeetingLineHeight] = useState(45 * hoursList.length + 10); //(45(time duration gap height) * 5 (hoursList entries) + 10 (margin))
    function getHoursList(start, end) {
        return new Array((end + 2 - start) / 2)
            .fill(0)
            .map((item, index) => start + index * timeLineGap);
    }
    useEffect(() => {
        const timeUpdateInterval = setInterval(() => {
            const curMoment = moment().tz(timeZone).clone();
            setCurrentMoment(curMoment);
            const timelineWindow = getTimelineWindow(curMoment);
            setStartHour(timelineWindow.startHour);
            setEndHour(timelineWindow.endHour);
            setStartTime(timelineWindow.startTime);
            // Sets the timeline end time.
            const hourList = getHoursList(timelineWindow.startHour, timelineWindow.endHour);
            setMaxMeetingLineHeight(45 * hourList.length + 10);
            // Sets list of hours in the time line.
            setHoursList(hourList);
        }, 1000);
        return () => clearInterval(timeUpdateInterval);
    }, []);
    return (_jsx("div", Object.assign({ className: `${className} ${status}` }, { children: _jsx("ul", { children: _jsxs("div", Object.assign({ className: "calendar" }, { children: [_jsx("div", Object.assign({ className: "time-slot" }, { children: hoursList &&
                            hoursList.map((selectedHour) => {
                                var hourMoement = moment()
                                    .tz(timeZone)
                                    .clone()
                                    .hours(selectedHour)
                                    .startOf('hour');
                                return (_jsxs("li", Object.assign({ className: selectedHour <= currentTimelineHour ? 'past-hour' : '' }, { children: [_jsx("span", Object.assign({ className: "hour" }, { children: hourMoement.format(simpleTimeFormat).slice(0, -2) +
                                                t(hourMoement.format(simpleTimeFormat).slice(-2)) })), _jsx("span", { className: "line", style: { height: `1px` } })] }), selectedHour));
                            }) })), _jsxs("li", Object.assign({ className: "current-time in-calendar", style: {
                            top: `${currentMoment.diff(startTime, 'minutes') / minutesDivider}px`,
                        } }, { children: [_jsx("span", { className: "hour hour-spacer" }), _jsx("span", { className: `line ${status}` })] })), _jsx("div", Object.assign({ className: "meeting" }, { children: hoursList &&
                            meetings.map((meeting) => {
                                const { start, end } = meeting;
                                const endHourMoment = moment()
                                    .tz(timeZone)
                                    .clone()
                                    .hours(endHour)
                                    .startOf('hour');
                                if (end.isBefore(startTime) || start.isAfter(endHourMoment)) {
                                    return;
                                }
                                const startHourMoment = moment()
                                    .tz(timeZone)
                                    .clone()
                                    .hours(startHour)
                                    .startOf('hour');
                                // Clamp meetings to the visible timeline window so partial
                                // meetings render only for the displayed portion.
                                const visibleMeetingStart = moment.max(start, startHourMoment);
                                const visibleMeetingEnd = moment.min(end, endHourMoment);
                                const minutes = visibleMeetingEnd.diff(visibleMeetingStart, 'minutes');
                                const startMeetingMinutesDiff = visibleMeetingStart.diff(startHourMoment, 'minutes');
                                if (minutes <= 0) {
                                    return null;
                                }
                                const meetingTimeHeight = startMeetingMinutesDiff > 0
                                    ? startMeetingMinutesDiff / minutesDivider + 10
                                    : 10;
                                var height = Math.min(minutes / minutesDivider, maxMeetingLineHeight);
                                height =
                                    height + meetingTimeHeight > maxMeetingLineHeight
                                        ? height - meetingTimeHeight
                                        : height;
                                return (_jsx("span", Object.assign({ className: "line", style: {
                                        height: `${height}px`,
                                        top: `${meetingTimeHeight}px`,
                                        marginLeft: window.innerHeight > window.innerWidth
                                            ? '0.8vw'
                                            : widgetCount !== 0
                                                ? '0vw'
                                                : '3.2vw',
                                    } }, { children: t('floor.room-details-popup.booked') }), `${start.valueOf()}-${end.valueOf()}`));
                            }) }))] })) }) })));
}
TimeLine.propTypes = {
    className: PropTypes.string.isRequired,
    date: PropTypes.object,
    meetings: PropTypes.array,
    timeLineLimit: PropTypes.number,
    timeLineGap: PropTypes.number,
    timeLineUnits: PropTypes.string,
    status: PropTypes.string,
    simpleTimeFormat: PropTypes.string,
    currentTimeFormat: PropTypes.string,
    minutesDivider: PropTypes.number,
};
TimeLine.defaultProps = {
    date: moment(),
    meetings: [],
    timeLineLimit: 12,
    timeLineGap: 2,
    timeLineUnits: 'hours',
    simpleTimeFormat: 'hA',
    currentTimeFormat: 'hh:mmA',
    status: 'Available',
    minutesDivider: 2.67,
};
export default TimeLine;
